import { useMemo } from 'react'
import { useTheme } from './useTheme'

/**
 * CSS custom property names from `tokens.css` that resolve to a usable color or
 * shadow value for non-CSS contexts (SVG / recharts presentation attributes).
 * When adding a token to `tokens.css`, add it here too if chart/SVG code needs
 * it — there is no automated check tying the two files together, but
 * `useThemeColors` warns in dev when a key resolves to nothing.
 *
 * Gradients (`--gradient-card`, `--gradient-accent`) are deliberately excluded:
 * a CSS gradient string is not a valid SVG paint value, so they are only usable
 * in CSS contexts where `var()` works directly.
 */
export const SEMANTIC_TOKEN_KEYS = [
  // Status (reserved meanings — always paired with an icon + label)
  '--status-good',
  '--status-warning',
  '--status-serious',
  '--status-critical',
  '--status-good-fg',
  '--status-warning-fg',
  '--status-serious-fg',
  '--status-critical-fg',
  // Legacy semantic aliases, still referenced by existing components
  '--color-success',
  '--color-warning',
  '--color-error',
  '--color-info',
  '--color-active',
  '--color-focus-ring',
  '--color-text-secondary',
  // Elevation
  '--shadow-card',
  '--shadow-glow-accent',
  // Chart: categorical identity. Assign IN ORDER, never cycled.
  '--chart-slot-1',
  '--chart-slot-2',
  '--chart-slot-3',
  '--chart-slot-4',
  '--chart-slot-5',
  '--chart-slot-6',
  '--chart-slot-7',
  '--chart-slot-8',
  // Chart: status-meaning series (good / warning / bad), not categorical
  '--chart-series-1',
  '--chart-series-2',
  '--chart-series-3',
  // Chart chrome
  '--chart-neutral',
  '--chart-grid',
  '--chart-axis',
  // Deltas — keyed by intent, never by direction
  '--delta-favorable',
  '--delta-adverse',
  '--delta-neutral',
] as const

export type SemanticTokenKey = (typeof SEMANTIC_TOKEN_KEYS)[number]

/** camelCase resolved values for every key in {@link SEMANTIC_TOKEN_KEYS}. */
export interface ThemeColorTokens {
  statusGood: string
  statusWarning: string
  statusSerious: string
  statusCritical: string
  statusGoodFg: string
  statusWarningFg: string
  statusSeriousFg: string
  statusCriticalFg: string
  colorSuccess: string
  colorWarning: string
  colorError: string
  colorInfo: string
  colorActive: string
  colorFocusRing: string
  colorTextSecondary: string
  shadowCard: string
  shadowGlowAccent: string
  chartSlot1: string
  chartSlot2: string
  chartSlot3: string
  chartSlot4: string
  chartSlot5: string
  chartSlot6: string
  chartSlot7: string
  chartSlot8: string
  chartSeries1: string
  chartSeries2: string
  chartSeries3: string
  chartNeutral: string
  chartGrid: string
  chartAxis: string
  deltaFavorable: string
  deltaAdverse: string
  deltaNeutral: string
}

/**
 * Categorical chart slots in assignment order. Read this rather than indexing
 * the token names by hand, so the "fixed order, never cycled" rule is a
 * property of the code and not of every caller's discipline.
 *
 * A ninth series is NOT a generated hue — fold it into "Other" or facet.
 */
export function categoricalSlots(tokens: ThemeColorTokens): string[] {
  return [
    tokens.chartSlot1,
    tokens.chartSlot2,
    tokens.chartSlot3,
    tokens.chartSlot4,
    tokens.chartSlot5,
    tokens.chartSlot6,
    tokens.chartSlot7,
    tokens.chartSlot8,
  ]
}

/**
 * From this slot onward a chart owes a relief channel — visible direct labels or
 * a table view. Not dismissable; see the design doc §3.4.
 *
 * Measured against the card surface charts actually render on: in light mode
 * slot-4 (brass, 2.30:1) and slot-5 (coral, 2.85:1) fall below 3:1; in dark mode
 * slot-6 (sea green, 2.92:1) does. Slot 4 is therefore the safe threshold for
 * both modes.
 */
export const RELIEF_REQUIRED_FROM_SLOT = 4

function toCamelCase(cssVarName: string): string {
  return cssVarName.replace(/^--/, '').replace(/-([a-z0-9])/g, (_, char: string) => char.toUpperCase())
}

/**
 * Resolves the active theme's semantic CSS variables into plain strings.
 *
 * recharts renders colors as SVG presentation attributes (e.g. `fill="..."`),
 * which cannot consume `var(--status-good)` expressions. This hook reads the
 * computed values off `document.documentElement` instead, so chart components
 * can pass resolved color strings. Memoized; only re-read when the mode changes.
 */
export function useThemeColors(): ThemeColorTokens {
  const { mode } = useTheme()

  return useMemo(() => {
    const styles = getComputedStyle(document.documentElement)
    const resolved: Record<string, string> = {}
    for (const key of SEMANTIC_TOKEN_KEYS) {
      const value = styles.getPropertyValue(key).trim()
      if (import.meta.env.DEV && !value) {
        console.warn(`useThemeColors: ${key} resolved to nothing for theme "${mode}" — check tokens.css`)
      }
      resolved[toCamelCase(key)] = value
    }
    return resolved as unknown as ThemeColorTokens
  }, [mode])
}
